import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { getOptimizedStorageImageUrl } from "@/lib/utils";

interface SidebarRelatedArticlesProps {
  currentArticleId: string;
  category: string | null;
  tags: string[] | null;
  pinnedIds?: string[];
}

const SidebarRelatedArticles = ({ currentArticleId, category, tags, pinnedIds = [] }: SidebarRelatedArticlesProps) => {
  const { data: articles, isLoading } = useQuery({
    queryKey: ['sidebar-related', currentArticleId, category, tags, pinnedIds],
    queryFn: async () => {
      let results: { id: string; title: string; slug: string; featured_image: string | null; category: string | null }[] = [];

      // First, fetch pinned articles if any
      if (pinnedIds.length > 0) {
        const { data } = await supabase
          .from('articles')
          .select('id, title, slug, featured_image, category')
          .eq('status', 'published')
          .in('id', pinnedIds);
        if (data) {
          // Preserve pinned order
          results = pinnedIds
            .map((pid) => data.find((a) => a.id === pid))
            .filter(Boolean) as typeof results;
        }
      }

      // Fill remaining slots with tag/category matches
      if (results.length < 5 && tags && tags.length > 0) {
        const existingIds = [currentArticleId, ...results.map(a => a.id)];
        const { data } = await supabase
          .from('articles')
          .select('id, title, slug, featured_image, category')
          .eq('status', 'published')
          .not('id', 'in', `(${existingIds.join(',')})`)
          .overlaps('tags', tags)
          .limit(5 - results.length);
        if (data) results = [...results, ...data];
      }

      if (results.length < 5 && category) {
        const existingIds = [currentArticleId, ...results.map(a => a.id)];
        const { data } = await supabase
          .from('articles')
          .select('id, title, slug, featured_image, category')
          .eq('status', 'published')
          .eq('category', category)
          .not('id', 'in', `(${existingIds.join(',')})`)
          .limit(5 - results.length);
        if (data) results = [...results, ...data];
      }

      return results.slice(0, 5);
    },
    enabled: !!(category || (tags && tags.length > 0) || pinnedIds.length > 0),
  });

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4">
        <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">Related Articles</h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3">
              <Skeleton className="w-16 h-16 rounded shrink-0" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!articles || articles.length === 0) return null;

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">Related Articles</h3>
      <div className="space-y-3">
        {articles.map((article) => (
          <Link
            key={article.id}
            to={`/articles/${article.slug}`}
            className="flex gap-3 group"
          >
            {article.featured_image ? (
              <img
                src={getOptimizedStorageImageUrl(article.featured_image, {
                  width: 64,
                  height: 64,
                  resize: "cover",
                  quality: 75,
                })}
                alt={article.title}
                className="w-16 h-16 rounded object-cover shrink-0"
                loading="lazy"
                width="64"
                height="64"
              />
            ) : (
              <div className="w-16 h-16 rounded bg-muted shrink-0" />
            )}
            <p className="text-sm text-foreground line-clamp-3 group-hover:text-primary transition-colors leading-snug">
              {article.title}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SidebarRelatedArticles;
